import { useState, useEffect, useCallback } from 'react';
import { Epic, Story, Sprint } from '../types';
import { connectJiraApi } from '../services/connectJiraApi';
import { connectContext } from '../services/connectContext';

interface JiraDataState {
  epics: Epic[];
  stories: Story[];
  sprints: Sprint[];
  loading: boolean;
  error: string | null;
  connected: boolean;
}

interface UseConnectJiraDataReturn extends JiraDataState {
  connect: () => Promise<void>;
  disconnect: () => void;
  refreshData: () => Promise<void>;
  logWork: (issueKey: string, timeSpent: string, comment?: string) => Promise<void>;
  updateIssueStatus: (issueKey: string, status: string) => Promise<void>;
}

export const useConnectJiraData = (): UseConnectJiraDataReturn => {
  const [state, setState] = useState<JiraDataState>({
    epics: [],
    stories: [],
    sprints: [],
    loading: false,
    error: null,
    connected: false
  });

  // Check if we're in a Connect environment and auto-load data
  useEffect(() => {
    const initializeConnect = async () => {
      if (connectContext.isConnectEnvironment()) {
        // Wait for context to be loaded
        const waitForContext = () => {
          return new Promise<void>((resolve) => {
            const check = () => {
              if (connectContext.getContext() && connectContext.getJiraBaseUrl()) {
                resolve();
              } else {
                setTimeout(check, 100);
              }
            };
            check();
          });
        };

        try {
          await waitForContext();
          setState(prev => ({ ...prev, connected: true }));
          await loadData();
        } catch (error) {
          setState(prev => ({
            ...prev,
            error: 'Failed to initialize Connect environment',
            connected: false
          }));
        }
      }
    };

    initializeConnect();
  }, []);

  const loadData = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Get boards first
      const boards = await connectJiraApi.getBoards();
      if (boards.length === 0) {
        throw new Error('No Scrum boards found for this project');
      }

      const boardId = boards[0].id;

      // Load data in parallel
      const [epics, sprints] = await Promise.all([
        connectJiraApi.getEpics(),
        connectJiraApi.getSprints(boardId)
      ]);

      // Load stories for each epic and sprint
      const epicStoriesPromises = epics.map(epic => 
        connectJiraApi.getEpicStories(epic.key)
      );
      
      const sprintStoriesPromises = sprints.map(sprint => 
        connectJiraApi.getSprintIssues(boardId, sprint.id)
      );

      const [epicStoriesArrays, sprintStoriesArrays] = await Promise.all([
        Promise.all(epicStoriesPromises),
        Promise.all(sprintStoriesPromises)
      ]);

      // Combine all stories and remove duplicates
      const allStories = new Map<string, Story>();
      
      epicStoriesArrays.forEach((stories, index) => {
        epics[index].stories = stories;
        stories.forEach(story => allStories.set(story.id, story));
      });

      sprintStoriesArrays.forEach((stories, index) => {
        sprints[index].stories = stories.map(s => s.id);
        sprints[index].totalPoints = stories.reduce((sum, s) => sum + s.storyPoints, 0);
        sprints[index].completedPoints = stories
          .filter(s => s.status === 'done')
          .reduce((sum, s) => sum + s.storyPoints, 0);
        sprints[index].velocity = sprints[index].status === 'completed' 
          ? sprints[index].completedPoints 
          : 0;
        
        stories.forEach(story => allStories.set(story.id, story));
      });

      setState(prev => ({
        ...prev,
        epics,
        stories: Array.from(allStories.values()),
        sprints,
        loading: false
      }));

      // Auto-resize iframe to fit content
      connectContext.resizeIframe();

    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load Jira data'
      }));
    }
  };

  const refreshData = useCallback(async () => {
    if (!connectContext.isConnectEnvironment()) {
      throw new Error('Not in Connect environment');
    }
    await loadData();
  }, []);

  const logWork = useCallback(async (issueKey: string, timeSpent: string, comment?: string) => {
    if (!connectContext.isConnectEnvironment()) {
      throw new Error('Not in Connect environment');
    }
    
    try {
      await connectJiraApi.logWork(issueKey, timeSpent, comment);
      await refreshData(); // Refresh to get updated time tracking
    } catch (error) {
      throw new Error(`Failed to log work: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [refreshData]);

  const updateIssueStatus = useCallback(async (issueKey: string, status: string) => {
    if (!connectContext.isConnectEnvironment()) {
      throw new Error('Not in Connect environment');
    }
    
    try {
      // This would need to map status to transition IDs
      // For now, we'll just refresh the data
      await refreshData();
    } catch (error) {
      throw new Error(`Failed to update issue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [refreshData]);

  const connect = useCallback(async () => {
    // In Connect environment, connection is automatic
    // Just refresh the data
    if (connectContext.isConnectEnvironment()) {
      await refreshData();
    }
  }, [refreshData]);

  const disconnect = useCallback(() => {
    // In Connect environment, we can't really disconnect
    // Just clear the state
    setState({
      epics: [],
      stories: [],
      sprints: [],
      loading: false,
      error: null,
      connected: false
    });
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    refreshData,
    logWork,
    updateIssueStatus
  };
};